import classNames from "classnames"
import { useRouter } from "next/router"
import { useMemo, useState } from "react"

import { useTranslation } from "@bounce/i18n"
import type { FormSelectProps } from "@bounce/web-components"
import { Button, FormSelect } from "@bounce/web-components"

import type { FormattedBlogPostPreview } from "@/libs/prismic/types"
import { blogPostIncludes } from "@/utils/blogPost"

type Props = {
  articleData: FormattedBlogPostPreview[]
  className?: string
}

export const SearchBar = ({ articleData, className }: Props) => {
  const { t } = useTranslation("marketing-generic")

  const router = useRouter()

  const [query, setQuery] = useState("")

  const options = useMemo(() => {
    if (!query) return []

    return articleData
      .filter(post => blogPostIncludes(post, query))
      .map(post => ({
        ...post,
        id: post.uid,
        subtitle: post.excerpt,
      }))
  }, [articleData, query]).slice(0, 4)

  const firstOption = options[0]

  const onChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const query = ev.currentTarget.value
    setQuery(query)
  }

  const onSearch = (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!firstOption) return

    void router.push(firstOption.url)
  }

  const onSelectRecommendation: FormSelectProps["onClickOption"] = option => {
    const post = articleData.find(a => a.uid === option.id)
    if (!post) return

    void router.push(post.url)
  }

  return (
    <form onSubmit={onSearch} className={classNames("flex w-full flex-col", className)}>
      <FormSelect
        defaultValue=""
        placeholder={t("cmp.blogSearchBox.placeholderV2", "Search guides, posts and keywords")}
        onChange={onChange}
        options={options}
        onClickOption={onSelectRecommendation}
      />
      <Button
        as="button"
        onClick={onSearch}
        color="primary"
        className="mt-4 w-full max-w-full md:mt-3.5 md:h-16 md:w-fit lg:!text-2xl xl:mt-4"
        textClassName="line-clamp-1">
        {t("cmp.blogSearchBox.cta", "Search")}
      </Button>
    </form>
  )
}
